import _ from 'lodash';

import { FieldInputType } from 'constants/fields';
import { NationalHousingActSection } from 'constants/loanApplication';
import { formatNumberToCurrencyString } from 'utils/number';

import {
  getAdditionalLoanInformationConfig,
  getLoanDetailsConfig,
} from '../../tabs/propertyAndLoan/EditModal/Content/MainContent/LoanInfo/LoanInfo.config';
import {
  customAdjustmentsConfigFields,
  lockExtensionFields,
} from '../../tabs/propertyAndLoan/EditModal/Content/MainContent/LockInfo/LockInfo.config';
import { getPropertyConfig } from '../../tabs/propertyAndLoan/EditModal/Content/MainContent/Property/Property.config';
import { SearchCategory } from '../UniversalSearch.constants';

import {
  LOCK_INFO_SECTION_CONFIG,
  STATIC_FIELD_SECTION_CONFIGS,
  VISIBILITY_FILTERED_SECTION_CONFIGS,
} from './fields.config';
import { FIELDS_PARENT_TAB } from './fields.constants';

const formatFieldValue = (value, field) => {
  if (_.isNil(value) || value === '') {
    return '';
  }

  if (_.isBoolean(value)) {
    return value ? 'Yes' : 'No';
  }

  const inputType = field?.inputProps?.type;

  if (inputType === FieldInputType.CURRENCY && _.isNumber(value)) {
    return formatNumberToCurrencyString(value, { decimalScale: 0 });
  }

  const options = field?.inputProps?.options || field?.inputProps?.staticOptions;

  if (options?.length) {
    const matched = options.find(opt => opt.value === value);

    return matched?.name || String(value);
  }

  return String(value);
};

const OWNER_FIELD_IDS = new Set(['owners', 'owner']);

const stripEntityPrefix = nameInModel => {
  if (!nameInModel) {
    return '';
  }

  const match = nameInModel.match(/^\[.*?\]\.(.+)$/);

  return match ? match[1] : nameInModel;
};

export const extractSearchableFields = configFields =>
  _.uniqBy(
    Object.values(configFields)
      .filter(
        field =>
          field?.fieldId &&
          field?.inputProps?.label &&
          !OWNER_FIELD_IDS.has(field.fieldId) &&
          !field.inputProps.className?.includes('visually-hidden'),
      )
      .map(({ fieldId, inputProps, nameInModel }) => ({
        fieldId,
        label: inputProps.label,
        dataPath: stripEntityPrefix(nameInModel),
      })),
    'fieldId',
  );

export const resolveFieldValue = ({ dataSource, dataPath, field }) => {
  if (!dataSource || !dataPath) {
    return '';
  }

  const rawValue = _.get(dataSource, dataPath);

  return !_.isNil(rawValue) ? formatFieldValue(rawValue, field) : '';
};

const extractFieldsFromConfig = ({
  configFields,
  sectionName,
  parentTab,
  entityType,
  sectionHref,
  visibleFieldIds,
  dataSource,
}) =>
  Object.values(configFields)
    .filter(field => field?.inputProps?.label)
    .filter(field => !visibleFieldIds || visibleFieldIds.has(field.fieldId))
    .map(field => {
      const rawValue =
        dataSource && field.nameInModel ? _.get(dataSource, field.nameInModel) : null;
      const fieldValue = !_.isNil(rawValue) ? formatFieldValue(rawValue, field) : '';

      return {
        id: `field-${entityType}-${sectionHref}-${field.fieldId}`,
        label: field.inputProps.label.replace(/:$/, ''),
        category: SearchCategory.FIELDS,
        breadcrumb: `${parentTab} > ${sectionName}`,
        fieldId: field.fieldId,
        tabRoute: _.kebabCase(FIELDS_PARENT_TAB),
        entityType,
        sectionHref,
        ...(fieldValue ? { fieldValue } : {}),
      };
    });

const collectFieldIdsFromConfig = config => {
  const fields = (config?.groups ?? []).flatMap(group => group?.fields ?? []);

  return new Set(fields.filter(field => field?.fieldId).map(field => field.fieldId));
};

const collectFieldIdsFromStaticConfigs = staticConfigs => {
  const ids = new Set();

  staticConfigs.forEach(configFields => {
    Object.values(configFields).forEach(field => {
      if (field?.fieldId) {
        ids.add(field.fieldId);
      }
    });
  });

  return ids;
};

export const buildFieldConfigsAndVisibility = ({
  isPurchase,
  isRefinance,
  isConstructionToPermanent,
  loanPurpose,
  mortgageType,
  isLoanNoPpeEngineType,
  isLoanLocked,
  propertyState,
}) => {
  const propertyGeneratedConfig = getPropertyConfig({
    addressCounties: [],
    isMilitaryAddressAllowed: true,
    isPurchase,
    isDeedRestricted: true,
    isConstructionToPermanent,
    isRefinance,
    isMultiUnit: true,
    isLeasehold: true,
    isApplicationTbd: false,
    isSubjectProperty: false,
    onChangeAddressState: _.noop,
    state: propertyState,
    isMixedUseVisible: true,
    isManufacturedHome: true,
    isCompletionDateVisible: true,
  });

  const loanDetailsGeneratedConfig = getLoanDetailsConfig({
    loanPurpose,
    shouldShowTotalLoanAmount: true,
    mortgageLienPriorityOptions: [],
    mortgageType,
    insuranceEscrowIds: [],
    isRefinanceCashOutSubflow: isRefinance,
    shouldRecalculateCashOutAmount: false,
  });

  const additionalLoanInfoGeneratedConfig = getAdditionalLoanInformationConfig({
    programIdentifiersOptions: [],
    investorFeatureCodesOptions: [],
    leadSourcesOptions: [],
    isLoanNoPpeEngineType,
    loanWorkflow: 'placeholder',
    mortgageType,
    loanPurpose,
    housingFinanceAgenciesOptions: [],
    isOtherHousingFinanceAgency: true,
    isLoanLocked,
    nationalHousingActSection:
      NationalHousingActSection.SECTION_203K_FHA_REHABILITATION_LOAN_INSURANCE,
  });

  const dynamicFieldIds = [
    propertyGeneratedConfig,
    loanDetailsGeneratedConfig,
    additionalLoanInfoGeneratedConfig,
  ].reduce((ids, config) => {
    collectFieldIdsFromConfig(config).forEach(id => ids.add(id));

    return ids;
  }, new Set());

  const staticFieldIds = collectFieldIdsFromStaticConfigs([
    customAdjustmentsConfigFields,
    lockExtensionFields,
  ]);

  const visibleFieldIds = new Set([...dynamicFieldIds, ...staticFieldIds]);

  return {
    visibleFieldIds,
    lockInfoConfigFields: {
      ...customAdjustmentsConfigFields,
      ...lockExtensionFields,
    },
  };
};

export const buildFieldsIndex = ({
  lockInfoConfigFields,
  visibleFieldIds,
  subjectProperty,
  loanApplicationData,
}) => {
  const propertyDataSource = {
    ...subjectProperty?.propertyAndOccupancy,
    ...subjectProperty?.propertyAddress,
  };

  const dataSources = {
    subjectProperty,
    loanApplicationData,
    propertyDataSource,
  };

  const lockInfoSectionConfig = {
    ...LOCK_INFO_SECTION_CONFIG,
    configFields: lockInfoConfigFields,
  };

  const allVisibilityFilteredConfigs = [
    ...VISIBILITY_FILTERED_SECTION_CONFIGS,
    lockInfoSectionConfig,
  ];

  const visibilityFilteredItems = allVisibilityFilteredConfigs.flatMap(
    ({ configFields, sectionName, entityType, sectionHref, dataSourceKey }) =>
      extractFieldsFromConfig({
        configFields,
        sectionName,
        parentTab: FIELDS_PARENT_TAB,
        entityType,
        sectionHref,
        dataSource: dataSources[dataSourceKey],
        visibleFieldIds,
      }),
  );

  const staticItems = STATIC_FIELD_SECTION_CONFIGS.flatMap(
    ({ configFields, sectionName, entityType, sectionHref, dataSourceKey }) =>
      extractFieldsFromConfig({
        configFields,
        sectionName,
        parentTab: FIELDS_PARENT_TAB,
        entityType,
        sectionHref,
        dataSource: dataSources[dataSourceKey],
      }),
  );

  return [...visibilityFilteredItems, ...staticItems];
};
